"use client";

import { useEffect, useRef, useState } from "react";

// Verifone Checkout inline payment form — shared by the booking checkout and the
// gift-voucher checkout so the payment step looks and behaves identically.
//
// Injects the loader <script> as a child of the container div; with no containerId
// param the SDK mounts the hosted card iframe at the script's parentElement (this
// div). A spinner shows until the iframe's first iFrameSizer message arrives. The
// session is created WITHOUT a return_url, so a declined card stays on the form for
// retry and the parent page detects the outcome by polling its status endpoint.
//
// Diagnostics (14 Oct 2026 incident — the form failed to load for some guests and nothing
// recorded it): onReady fires once with the load time, onSlow once if the form is still not
// up after SLOW_MS, and onScriptError receives the time to failure. All optional. Each load
// attempt gets its own id (2nd argument) so reports from one attempt can be told apart from
// a retry's.
const SLOW_MS = 15_000;

export function VerifonePaymentFrame({
  iframeUrl,
  onScriptError,
  onReady,
  onSlow,
}: {
  iframeUrl: string;
  onScriptError?: (ms: number, attempt: string) => void;
  onReady?: (ms: number, attempt: string) => void;
  onSlow?: (ms: number, attempt: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  // Keep the latest onScriptError without putting it in the mount effect's deps —
  // it's an inline arrow from the parent (new ref every render), and including it
  // would re-run the effect on every parent render, tearing down + re-injecting the
  // loader script. That race makes loader.js fail with "Invalid jsclient container".
  const onErrRef = useRef(onScriptError);
  useEffect(() => { onErrRef.current = onScriptError; }, [onScriptError]);
  const onReadyRef = useRef(onReady);
  useEffect(() => { onReadyRef.current = onReady; }, [onReady]);
  const onSlowRef = useRef(onSlow);
  useEffect(() => { onSlowRef.current = onSlow; }, [onSlow]);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;
    setReady(false);
    container.innerHTML = ""; // clear any previous attempt
    const started = performance.now();
    const elapsed = () => Math.round(performance.now() - started);
    const attempt = Math.random().toString(36).slice(2, 10);
    // `active` goes false in cleanup. A removed <script> keeps loading, so a superseded
    // attempt could otherwise fail later and tear down (or misreport) the form that replaced it.
    let active = true;
    let slowTimer: number | undefined;
    const settle = () => {
      window.clearTimeout(slowTimer);
      window.removeEventListener("message", onMsg);
    };
    const script = document.createElement("script");
    script.src = iframeUrl; // no containerId → SDK mounts at script.parentElement
    script.async = true;
    script.onerror = () => {
      if (!active) return;
      settle();
      onErrRef.current?.(elapsed(), attempt);
    };
    container.appendChild(script);

    slowTimer = window.setTimeout(() => {
      if (active) onSlowRef.current?.(elapsed(), attempt);
    }, SLOW_MS);

    function onMsg(e: MessageEvent) {
      if (!active) return;
      if (typeof e.data === "string" && e.data.startsWith("[iFrameSizer]")) {
        settle();
        setReady(true);
        onReadyRef.current?.(elapsed(), attempt);
      }
    }
    window.addEventListener("message", onMsg);
    return () => {
      active = false;
      script.onerror = null;
      settle();
      container.innerHTML = "";
    };
  }, [iframeUrl]); // only re-mount when the session URL changes

  return (
    <div className="relative overflow-hidden rounded-2xl">
      {!ready && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-cream-warm">
          <div className="h-7 w-7 animate-spin rounded-full border-2 border-ink/20 border-t-ink" />
          <p className="text-[13px] font-light text-ink-soft">Loading payment form…</p>
        </div>
      )}
      <div ref={ref} className="min-h-[400px]" />
    </div>
  );
}
